/**
 * 图层面板（M3-02 / DESIGN.md §7.1）。
 *
 * 纯展示组件：文档树进、回调出，不认识 store（分层规则）。
 * 行序 = 视觉序：children 末位在最上，渲染时反转。
 * 顶部是作用于**活动图层**的不透明度滑杆与混合模式下拉（PS 布局惯例）。
 */
import type { JSX } from 'solid-js';
import { For, Show, createEffect, createSignal } from 'solid-js';
import type { BlendMode, Document, Layer } from '../../document/types';
import { Icon } from '../icons/Icon';
import styles from './LayersPanel.module.css';

/**
 * 下拉里展示的全部 27 种模式，按 PS 下拉菜单的六组分隔线顺序分组
 * （文档模型的 `BlendMode` 全集——组内顺序跟组的划分是 UI 的事，
 * `document/types.ts` 里 `SEPARABLE_BLEND_MODES` 的顺序是着色器序号的
 * 约定，两者不必一致）。
 */
export const BLEND_MODE_GROUPS: ReadonlyArray<{
  readonly label: string;
  readonly options: ReadonlyArray<{ value: BlendMode; label: string }>;
}> = [
  {
    label: '正常',
    options: [
      { value: 'normal', label: '正常' },
      { value: 'dissolve', label: '溶解' },
    ],
  },
  {
    label: '变暗',
    options: [
      { value: 'darken', label: '变暗' },
      { value: 'multiply', label: '正片叠底' },
      { value: 'colorBurn', label: '颜色加深' },
      { value: 'linearBurn', label: '线性加深' },
      { value: 'darkerColor', label: '深色' },
    ],
  },
  {
    label: '变亮',
    options: [
      { value: 'lighten', label: '变亮' },
      { value: 'screen', label: '滤色' },
      { value: 'colorDodge', label: '颜色减淡' },
      { value: 'linearDodge', label: '线性减淡(添加)' },
      { value: 'lighterColor', label: '浅色' },
    ],
  },
  {
    label: '对比',
    options: [
      { value: 'overlay', label: '叠加' },
      { value: 'softLight', label: '柔光' },
      { value: 'hardLight', label: '强光' },
      { value: 'vividLight', label: '亮光' },
      { value: 'linearLight', label: '线性光' },
      { value: 'pinLight', label: '点光' },
      { value: 'hardMix', label: '实色混合' },
    ],
  },
  {
    label: '比较',
    options: [
      { value: 'difference', label: '差值' },
      { value: 'exclusion', label: '排除' },
      { value: 'subtract', label: '减去' },
      { value: 'divide', label: '划分' },
    ],
  },
  {
    label: '色彩',
    options: [
      { value: 'hue', label: '色相' },
      { value: 'saturation', label: '饱和度' },
      { value: 'color', label: '颜色' },
      { value: 'luminosity', label: '明度' },
    ],
  },
];

/** 扁平列表（不分组），历史上依赖它的调用点/测试继续可用。 */
export const BLEND_MODE_OPTIONS: ReadonlyArray<{ value: BlendMode; label: string }> =
  BLEND_MODE_GROUPS.flatMap((group) => group.options);

/**
 * `dragOver` 的两个哨兵值：拖一行到底部工具栏的删除/新建图层按钮上
 * （PS 惯例：拖到垃圾桶=删除，拖到新建图层图标=复制）——跟行到行的
 * 重排共用同一套指针事件机制（pointerdown 开始、pointerenter 更新
 * 悬停目标、全局 pointerup 收尾），只是落点语义从"换位置"变成
 * "对源图层做一个动作"，不值得为这两个按钮另开一套状态机。
 */
const TOOLBAR_DELETE = '@toolbar-delete';
const TOOLBAR_DUPLICATE = '@toolbar-duplicate';

const KIND_BADGE: Readonly<Record<Layer['kind'], string>> = {
  raster: '',
  group: '组',
  adjustment: '调',
  text: '文',
  fill: '填',
  shape: '形',
  smartObject: '智',
};

export interface LayersPanelProps {
  readonly document: Document | null;
  readonly activeLayerId: string | null;
  readonly onSelect: (id: string) => void;
  /**
   * 当前多选的图层 id（PS 的 shift 范围多选 / cmd 追加多选，供右键"合并
   * 图层"/多图层编组用）。缺省视为"只选中 activeLayerId 这一个"。
   */
  readonly selectedLayerIds?: ReadonlySet<string> | undefined;
  /** 多选变化时提交完整的新集合（shift 范围/cmd 追加都走这一个口子）。 */
  readonly onSelectionChange?: ((ids: readonly string[]) => void) | undefined;
  readonly onToggleVisible: (id: string, visible: boolean) => void;
  readonly onSetOpacity: (id: string, opacity: number) => void;
  readonly onSetBlendMode: (id: string, mode: BlendMode) => void;
  /** 图层缩略图 data URL；null = 该图层没有可视像素（M3-03）。 */
  readonly thumbnail?: ((id: string) => string | null) | undefined;
  /** 拖拽重排：把 sourceId 放到 targetId 当前的位置。 */
  readonly onReorder?: ((sourceId: string, targetId: string) => void) | undefined;
  /** Alt+点击行 = 切换剪贴蒙版（M2-13 命令）。 */
  readonly onToggleClipped?: ((id: string, clipped: boolean) => void) | undefined;
  /** 展开/收起一个组（只有 `kind: 'group'` 的行会渲染这个入口）。 */
  readonly onToggleCollapsed?: ((id: string, collapsed: boolean) => void) | undefined;
  /** 行内改名提交（Enter）。Esc 取消不回调。 */
  readonly onRename?: ((id: string, name: string) => void) | undefined;
  /** 删除这一个图层（PS 图层面板的垃圾桶图标，行内版；拖一行到底部工具栏的垃圾桶图标上也走这个回调）。 */
  readonly onDelete?: ((id: string) => void) | undefined;
  /** 复制这一个图层（拖一行到底部工具栏的"新建图层"图标上，PS 惯例）。 */
  readonly onDuplicateLayer?: ((id: string) => void) | undefined;
  /**
   * Cmd/Ctrl+点击缩略图 = 把该图层的 alpha 转成选区（PS 惯例）。
   * `modifiers` 转发 shift/alt，供调用方决定加选/减选/交叉。
   */
  readonly onLoadSelectionFromLayer?:
    | ((id: string, modifiers: { readonly shiftKey: boolean; readonly altKey: boolean }) => void)
    | undefined;
  /** 右键这一行（DESIGN.md §7.1 补充：右键菜单）。坐标是视口坐标，直接转发给 ContextMenu。 */
  readonly onContextMenu?: ((id: string, position: { readonly x: number; readonly y: number }) => void) | undefined;

  /* —— 底部工具栏（PS 惯例：新建图层/新建组/添加蒙版/删除图层）—— */
  /** 新建图层。缺省时按钮不渲染（同其余底部工具栏回调，未注入=没有这个功能）。 */
  readonly onNewLayer?: (() => void) | undefined;
  readonly onNewGroup?: (() => void) | undefined;
  /** 添加图层蒙版；`canAddMask` 决定按钮是否可点（已有蒙版/没有活动图层时置灰）。 */
  readonly onAddMask?: (() => void) | undefined;
  readonly canAddMask?: boolean | undefined;
  /** 删除当前活动图层（底部工具栏的垃圾桶——跟行内 `onDelete` 是两个不同的入口，
   * 但语义相同，调用方通常接到同一个处理函数）。 */
  readonly onDeleteActive?: (() => void) | undefined;
}

export function LayersPanel(props: LayersPanelProps): JSX.Element {
  const activeLayer = (): Layer | null => {
    const doc = props.document;
    if (doc === null || props.activeLayerId === null) return null;
    const find = (layers: readonly Layer[]): Layer | null => {
      for (const layer of layers) {
        if ((layer.id as string) === props.activeLayerId) return layer;
        if (layer.kind === 'group') {
          const inner = find(layer.children);
          if (inner !== null) return inner;
        }
      }
      return null;
    };
    return find(doc.root.children);
  };

  // 行内改名与拖拽重排都是纯 UI 态，提交时才回调
  const [renaming, setRenaming] = createSignal<{ id: string; value: string } | null>(null);
  const [dragSource, setDragSource] = createSignal<string | null>(null);
  const [dragOver, setDragOver] = createSignal<string | null>(null);

  /** shift 范围多选的锚点——不需要响应式，只在点击处理器里读写。 */
  let shiftAnchorId: string | null = null;

  /**
   * 方向键切行之后要把焦点带过去的目标 id——不能在按键的同一刻直接
   * `.focus()`：这个面板的行列表整体走"全局 version 信号驱动重渲染"这条
   * 既有模式（`main.tsx` 的 `layersDocument()`/`activeLayerId()` 都读同一个
   * 粗粒度 `version()` 信号），选中态一变，`<For>` 外层这整段 `rows(...)`
   * 表达式会被当成一个整体重新求值——不是精细更新属性，是整棵子树重建，
   * 于是刚 `.focus()` 上的那个节点几毫秒后就被替换掉，焦点跟着掉回
   * `document.body`（真实复现过：`sameNode` 断言为 false）。改成"先记下
   * 要聚焦谁，等 `activeLayerId` 真的变成这个 id 之后，在新长出来的那个
   * 节点上再 `.focus()`"，就不受这次重建影响。
   */
  const [pendingFocusId, setPendingFocusId] = createSignal<string | null>(null);
  createEffect(() => {
    const id = pendingFocusId();
    if (id === null || props.activeLayerId !== id) return;
    setPendingFocusId(null);
    queueMicrotask(() => {
      const el = document.querySelector<HTMLElement>(`[data-layer-id="${CSS.escape(id)}"]`);
      el?.focus();
    });
  });

  /** 图层 id 按视觉顺序（自上而下，跟 `rows` 的渲染顺序一致）展开成一个扁平列表。 */
  const visualOrder = (): string[] => {
    const out: string[] = [];
    const walk = (layers: readonly Layer[]): void => {
      for (const layer of [...layers].reverse()) {
        out.push(layer.id as string);
        if (layer.kind === 'group') walk(layer.children);
      }
    };
    if (props.document !== null) walk(props.document.root.children);
    return out;
  };

  const currentSelection = (): Set<string> =>
    new Set(props.selectedLayerIds ?? (props.activeLayerId === null ? [] : [props.activeLayerId]));

  /**
   * 点击一行时先判断是不是多选手势（shift 范围 / cmd 追加），是就走
   * `onSelectionChange`；不是就走原有的单选 `onSelect`，行为完全不变。
   * 返回 true 表示已经处理，调用方不用再走单选那条路。
   */
  const handleSelectionClick = (id: string, event: MouseEvent): boolean => {
    if (props.onSelectionChange === undefined) return false;
    if (event.shiftKey) {
      const order = visualOrder();
      const anchor = shiftAnchorId ?? id;
      const a = order.indexOf(anchor);
      const b = order.indexOf(id);
      if (a >= 0 && b >= 0) {
        const [lo, hi] = a <= b ? [a, b] : [b, a];
        props.onSelectionChange(order.slice(lo, hi + 1));
        return true;
      }
    }
    if (event.metaKey || event.ctrlKey) {
      const next = currentSelection();
      if (next.has(id)) next.delete(id);
      else next.add(id);
      shiftAnchorId = id;
      props.onSelectionChange([...next]);
      return true;
    }
    shiftAnchorId = id;
    return false;
  };

  const endDrag = (): void => {
    const source = dragSource();
    const target = dragOver();
    setDragSource(null);
    setDragOver(null);
    window.removeEventListener('pointerup', endDrag);
    if (source === null || target === null) return;
    if (target === TOOLBAR_DELETE) {
      props.onDelete?.(source);
      return;
    }
    if (target === TOOLBAR_DUPLICATE) {
      props.onDuplicateLayer?.(source);
      return;
    }
    if (source !== target) props.onReorder?.(source, target);
  };

  const rows = (layers: readonly Layer[], depth: number): JSX.Element => (
    // 反转：视觉自上而下 = 合成自上而下
    <For each={[...layers].reverse()}>
      {(layer) => (
        <>
          <div
            class={styles.row}
            classList={{
              [styles.active!]: (layer.id as string) === props.activeLayerId,
              [styles.multiSelected!]:
                (layer.id as string) !== props.activeLayerId &&
                (props.selectedLayerIds?.has(layer.id as string) ?? false),
              [styles.dropTarget!]:
                dragSource() !== null && dragOver() === (layer.id as string),
              [styles.clipped!]: layer.clipped,
            }}
            style={{ '--depth': depth }}
            role="option"
            aria-selected={(layer.id as string) === props.activeLayerId}
            data-testid={`layer-row-${layer.id}`}
            data-layer-id={layer.id as string}
            // 游走式 tabindex（WAI-ARIA listbox 惯例）：只有活动图层这一行能
            // 被 Tab 到，其余是 -1；键盘用户先 Tab 进面板一次，之后靠方向键
            // 在行间移动，不用每一行都占一个 Tab 停靠点（那样从图层面板
            // Tab 出去要按几十次）。
            tabIndex={(layer.id as string) === props.activeLayerId ? 0 : -1}
            onClick={(event) => {
              // Alt+点击 = 切换剪贴蒙版（PS 的分界线手势，行级简化）
              if (event.altKey) {
                props.onToggleClipped?.(layer.id as string, !layer.clipped);
                return;
              }
              if (handleSelectionClick(layer.id as string, event)) return;
              props.onSelect(layer.id as string);
            }}
            onKeyDown={(event) => {
              if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                event.preventDefault();
                const listbox = event.currentTarget.closest('[role="listbox"]');
                if (listbox === null) return;
                const options = Array.from(listbox.querySelectorAll<HTMLElement>('[role="option"]'));
                const index = options.indexOf(event.currentTarget);
                const next = options[event.key === 'ArrowDown' ? index + 1 : index - 1];
                const nextId = next === undefined ? undefined : next.dataset['layerId'];
                if (nextId !== undefined) {
                  // 不在这里直接 next.focus()——选中态一变，这一整段行列表会被
                  // `version()` 信号驱动整体重建（见上面 `pendingFocusId` 的
                  // 注释），刚聚焦的节点马上被换掉。改成记下目标 id，等新的
                  // 那个节点真的长出来之后再聚焦它。
                  setPendingFocusId(nextId);
                  props.onSelect(nextId);
                }
              } else if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                props.onSelect(layer.id as string);
              }
            }}
            onContextMenu={(event) => {
              event.preventDefault();
              // 右键一个不在当前多选里的行：先把它设为唯一选中，跟 PS 一致——
              // 右键菜单操作的应该是你刚右键的这一行，不是之前选中的别的行
              if (!(props.selectedLayerIds?.has(layer.id as string) ?? false)) {
                props.onSelect(layer.id as string);
              }
              props.onContextMenu?.(layer.id as string, { x: event.clientX, y: event.clientY });
            }}
            on:pointerdown={() => {
              if (renaming() !== null) return;
              setDragSource(layer.id as string);
              window.addEventListener('pointerup', endDrag);
            }}
            on:pointerenter={() => {
              if (dragSource() !== null) setDragOver(layer.id as string);
            }}
          >
            <Show
              when={layer.kind === 'group'}
              fallback={<span class={styles.disclosureSpacer} />}
            >
              <button
                type="button"
                class={styles.disclosure}
                aria-label={
                  (layer as Extract<Layer, { kind: 'group' }>).collapsed ? '展开组' : '收起组'
                }
                aria-expanded={!(layer as Extract<Layer, { kind: 'group' }>).collapsed}
                data-testid={`layer-collapse-${layer.id}`}
                // 行本身现在是可 Tab 到的 role="option"（见上面的游走式
                // tabindex）——这几个行内小按钮不再进 Tab 顺序，避免"可聚焦
                // 元素嵌在另一个可聚焦元素里"这条 axe serious 违规
                // （nested-interactive）；鼠标/触控点击不受影响
                tabIndex={-1}
                onClick={(event) => {
                  event.stopPropagation();
                  const group = layer as Extract<Layer, { kind: 'group' }>;
                  props.onToggleCollapsed?.(layer.id as string, !group.collapsed);
                }}
              >
                {(layer as Extract<Layer, { kind: 'group' }>).collapsed ? '▸' : '▾'}
              </button>
            </Show>
            <button
              type="button"
              class={styles.eye}
              aria-label={layer.visible ? '隐藏图层' : '显示图层'}
              aria-pressed={layer.visible}
              data-testid={`layer-visible-${layer.id}`}
              tabIndex={-1}
              onClick={(event) => {
                event.stopPropagation();
                props.onToggleVisible(layer.id as string, !layer.visible);
              }}
            >
              {/* PS 惯例：隐藏时这一格是空的，不是画个变灰的眼睛——
                  "眼睛一直显示"就是在描述灰眼睛看着跟没隐藏一个样 */}
              <Show when={layer.visible}>
                <Icon name="eye" size={16} />
              </Show>
            </button>
            <Show when={props.thumbnail?.(layer.id as string) ?? null}>
              {(url) => (
                <img
                  class={styles.thumb}
                  alt=""
                  src={url()}
                  data-testid={`layer-thumb-${layer.id}`}
                  onClick={(event) => {
                    // Cmd/Ctrl+点击缩略图＝转选区（PS 惯例），不当普通的选中图层点击
                    if (event.metaKey || event.ctrlKey) {
                      event.stopPropagation();
                      props.onLoadSelectionFromLayer?.(layer.id as string, {
                        shiftKey: event.shiftKey,
                        altKey: event.altKey,
                      });
                    }
                  }}
                />
              )}
            </Show>
            <Show when={KIND_BADGE[layer.kind] !== ''}>
              <span class={styles.badge}>{KIND_BADGE[layer.kind]}</span>
            </Show>
            <Show
              when={renaming()?.id !== (layer.id as string)}
              fallback={
                <input
                  class={styles.rename}
                  value={renaming()!.value}
                  data-testid="layer-rename"
                  ref={(el) => {
                    queueMicrotask(() => el.focus());
                  }}
                  onInput={(event) => {
                    setRenaming({ id: layer.id as string, value: event.currentTarget.value });
                  }}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      const next = renaming();
                      setRenaming(null);
                      if (next !== null && next.value !== layer.name && next.value !== '') {
                        props.onRename?.(next.id, next.value);
                      }
                    } else if (event.key === 'Escape') {
                      setRenaming(null);
                    }
                  }}
                  onBlur={() => {
                    setRenaming(null); // 失焦 = 取消，最不容易误改名
                  }}
                />
              }
            >
              <span
                class={styles.name}
                onClick={(event) => {
                  // 用 click 的 detail 计数当双击：两次点击间面板可能重渲染，
                  // 浏览器此时不一定合成 dblclick 事件，detail 却总是准的
                  if (event.detail >= 2) {
                    setRenaming({ id: layer.id as string, value: layer.name });
                  }
                }}
              >
                {layer.name}
              </span>
            </Show>
            <button
              type="button"
              class={styles.delete}
              aria-label="删除图层"
              title="删除图层"
              data-testid={`layer-delete-${layer.id}`}
              tabIndex={-1}
              onClick={(event) => {
                event.stopPropagation();
                props.onDelete?.(layer.id as string);
              }}
            >
              ×
            </button>
          </div>
          <Show when={layer.kind === 'group' && !layer.collapsed}>
            {rows((layer as Extract<Layer, { kind: 'group' }>).children, depth + 1)}
          </Show>
        </>
      )}
    </For>
  );

  return (
    <div class={styles.panel} data-testid="layers-panel">
      <Show
        when={props.document !== null}
        fallback={<p class={styles.empty}>尚无文档</p>}
      >
        <div class={styles.controls}>
          <select
            class={styles.blend}
            aria-label="混合模式"
            data-testid="layers-blend-mode"
            disabled={activeLayer() === null}
            value={activeLayer()?.blendMode ?? 'normal'}
            onChange={(event) => {
              const id = props.activeLayerId;
              if (id !== null) props.onSetBlendMode(id, event.currentTarget.value as BlendMode);
            }}
          >
            <For each={BLEND_MODE_GROUPS}>
              {(group) => (
                <optgroup label={group.label}>
                  <For each={group.options}>
                    {(option) => <option value={option.value}>{option.label}</option>}
                  </For>
                </optgroup>
              )}
            </For>
          </select>
          <label class={styles.opacity}>
            <span class={styles.opacityLabel}>不透明度</span>
            <input
              type="range"
              min="0"
              max="100"
              aria-label="不透明度"
              data-testid="layers-opacity"
              disabled={activeLayer() === null}
              value={Math.round((activeLayer()?.opacity ?? 1) * 100)}
              onChange={(event) => {
                const id = props.activeLayerId;
                if (id !== null) props.onSetOpacity(id, Number(event.currentTarget.value) / 100);
              }}
            />
            <span class={styles.opacityValue}>
              {Math.round((activeLayer()?.opacity ?? 1) * 100)}%
            </span>
          </label>
        </div>
        <div
          class={styles.list}
          role="listbox"
          aria-label="图层列表"
          // 活动图层为 null 时（比如顶层是文字/组图层，topmostRaster 找不到
          // 可以当"活动"的 raster），游走式 tabindex 落不到任何一行上，
          // 面板本身就成了键盘焦点顺序里的死角——退而求其次让容器自己可以
          // 被 Tab 到，一旦真的落上焦点就立刻转给第一行并选中它，行为上
          // 等价于「没有初始选中项的 listbox，Tab 进来默认落在第一项」
          tabIndex={props.activeLayerId === null ? 0 : -1}
          onFocus={(event) => {
            if (event.currentTarget !== event.target) return; // 冒泡自某一行的 focus，不是容器自己被 Tab 到
            const first = event.currentTarget.querySelector<HTMLElement>('[role="option"]');
            const firstId = first?.dataset['layerId'];
            if (firstId !== undefined) {
              // 同上面 `pendingFocusId` 的道理：选中态一变行列表就整体重建，
              // 这里也不能直接 `first.focus()`
              setPendingFocusId(firstId);
              props.onSelect(firstId);
            }
          }}
        >
          {rows(props.document!.root.children, 0)}
        </div>
        <div class={styles.toolbar}>
          <button
            type="button"
            class={styles.toolbarButton}
            aria-label="添加图层蒙版"
            title="添加图层蒙版"
            data-testid="layers-add-mask"
            disabled={props.onAddMask === undefined || props.canAddMask !== true}
            onClick={() => props.onAddMask?.()}
            on:pointerenter={() => {
              // 拖过来不是"删除"/"复制"这两个可投放的按钮：清掉悬停目标，
              // 免得松手时误触发它离开前经过的上一个按钮/行留下的旧目标
              if (dragSource() !== null) setDragOver(null);
            }}
          >
            <Icon name="mask" size={16} />
          </button>
          <button
            type="button"
            class={styles.toolbarButton}
            aria-label="新建组"
            title="新建组"
            data-testid="layers-new-group"
            disabled={props.onNewGroup === undefined}
            onClick={() => props.onNewGroup?.()}
            on:pointerenter={() => {
              if (dragSource() !== null) setDragOver(null);
            }}
          >
            <Icon name="folder" size={16} />
          </button>
          <div
            class={styles.toolbarSpacer}
            on:pointerenter={() => {
              if (dragSource() !== null) setDragOver(null);
            }}
          />
          <button
            type="button"
            class={styles.toolbarButton}
            classList={{ [styles.toolbarDropTarget!]: dragSource() !== null && dragOver() === TOOLBAR_DELETE }}
            aria-label="删除图层"
            title="删除图层（也可以把图层拖到这里）"
            data-testid="layers-delete-active"
            disabled={props.onDeleteActive === undefined || props.activeLayerId === null}
            onClick={() => props.onDeleteActive?.()}
            on:pointerenter={() => {
              if (dragSource() !== null) setDragOver(TOOLBAR_DELETE);
            }}
          >
            <Icon name="trash" size={16} />
          </button>
          <button
            type="button"
            class={styles.toolbarButton}
            classList={{ [styles.toolbarDropTarget!]: dragSource() !== null && dragOver() === TOOLBAR_DUPLICATE }}
            aria-label="新建图层"
            title="新建图层（把图层拖到这里可以复制它）"
            data-testid="layers-new-layer"
            disabled={props.onNewLayer === undefined}
            onClick={() => props.onNewLayer?.()}
            on:pointerenter={() => {
              if (dragSource() !== null) setDragOver(TOOLBAR_DUPLICATE);
            }}
          >
            <Icon name="plus" size={16} />
          </button>
        </div>
      </Show>
    </div>
  );
}
